import { ArrowPathIcon, MapPinIcon } from '@heroicons/react/24/solid';
import React, { useRef } from 'react';

import ParcelleSVG from '../../../../common/ParcelleSvg';
import type { Address, Parcelle, SelectedCopro } from '../../types';
import type { AutocompleteFeature } from '../BanSearch';
import BanSearch from '../BanSearch';

interface AddressPanelProps {
  selectedCopro: SelectedCopro | null;
  parcelles: Parcelle[];
  addresses: Address[];
  manualAddresses: Address[];
  onAddressCheck: (addressId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onAddManualAddress: (result: AutocompleteFeature) => void;
  onDeleteManualAddress: (addressId: string) => void;
  onAddressClick: (geometry: string, convertFromLambert?: boolean) => void;
  onSave: () => void;
  isSaving: boolean;
  showManualAddressInput: boolean;
  setShowManualAddressInput: (show: boolean) => void;
}

const extractNumberAndStreet = (address: string): [number, string] => {
  const parts = address.split(' ');
  const streetNumber = parseInt(parts[0], 10);
  const streetName = parts.slice(1).join(' ');
  return [streetNumber, streetName];
};

const sortAddresses = (addressesToSort: Address[]): Address[] => [...addressesToSort].sort((a, b) => {
  const [aNumber, aStreet] = extractNumberAndStreet(a.address);
  const [bNumber, bStreet] = extractNumberAndStreet(b.address);
  if (aStreet < bStreet) { return -1; }
  if (aStreet > bStreet) { return 1; }
  return aNumber - bNumber;
});

const AddressPanel: React.FC<AddressPanelProps> = ({
  selectedCopro,
  parcelles,
  addresses,
  manualAddresses,
  onAddressCheck,
  onSelectAll,
  onDeselectAll,
  onAddManualAddress,
  onDeleteManualAddress,
  onAddressClick,
  onSave,
  isSaving,
  showManualAddressInput,
  setShowManualAddressInput,
}) => {
  const scrollableRef = useRef<HTMLDivElement>(null);

  if (!selectedCopro) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
        <p className="text-sm text-gray-500">
          Sélectionnez une copro pour voir les adresses
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Header with select/deselect buttons */}
      <div className="flex-shrink-0 border-b bg-gray-50 p-2">
        <div className="flex gap-2">
          <button
            onClick={onSelectAll}
            className="flex-1 rounded bg-rgpd-blue-light px-2 py-1 text-xs text-white"
          >
            Tout sélectionner
          </button>
          <button
            onClick={onDeselectAll}
            className="flex-1 rounded bg-rgpd-blue-light px-2 py-1 text-xs text-white"
          >
            Tout désélectionner
          </button>
        </div>
      </div>

      {/* Scrollable address list */}
      <div ref={scrollableRef} className="min-h-0 flex-1 overflow-y-auto p-2">
        {/* Manual addresses section */}
        {manualAddresses.length > 0 && (
          <div className="mb-3 border-b pb-3">
            <h3 className="mb-2 text-sm font-semibold">Adresses manuelles</h3>
            {manualAddresses.map((address) => (
              <div key={address.id} className="mb-1 flex items-center">
                <input
                  type="checkbox"
                  id={`manual-${address.id}`}
                  checked={address.selected}
                  onChange={() => onDeleteManualAddress(address.id)}
                  className="mr-2 ml-1 cursor-pointer"
                />
                <label htmlFor={`manual-${address.id}`} className="flex items-center text-xs">
                  {address.geometry && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onAddressClick(address.geometry!, false);
                      }}
                      className="group relative mr-1 inline-flex items-center justify-center rounded-md bg-rgpd-blue-light/10 p-1 transition-all duration-150 hover:scale-110 hover:bg-rgpd-blue-light/30 focus:ring-2 focus:ring-rgpd-blue-light focus:ring-offset-1 focus:outline-none"
                      title="Centrer la carte sur cette adresse"
                    >
                      <MapPinIcon className="h-3 w-3 text-rgpd-blue-dark group-hover:text-rgpd-blue-dark" />
                      <span className="pointer-events-none absolute top-1/2 left-full z-50 ml-2 -translate-y-1/2 rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        Voir sur la carte
                      </span>
                    </button>
                  )}
                  <span className="truncate">
                    {address.address}, {address.postalCode} {address.city}
                  </span>
                </label>
              </div>
            ))}
          </div>
        )}

        {/* Manual address input */}
        <div className="mb-3 border-b pb-3">
          {showManualAddressInput ? (
            <div>
              <BanSearch onSelectResult={onAddManualAddress} searchHouseNumberOnly />
            </div>
          ) : (
            <div className="text-center">
              <p className="text-xs text-gray-600">Vous ne trouvez pas une adresse ?</p>
              <button
                onClick={() => setShowManualAddressInput(true)}
                className="text-xs text-rgpd-blue-light hover:text-rgpd-blue-dark"
              >
                Cliquez ici pour en ajouter une
              </button>
            </div>
          )}
        </div>

        {/* Parcelles and addresses */}
        <h3 className="mb-2 text-sm font-semibold">Parcelles sélectionnées</h3>
        {parcelles.map((parcelle) => {
          const parcelleAddresses = sortAddresses(addresses.filter((a) => a.parcelleId === parcelle.id));
          const selectedAddressCount = parcelleAddresses.filter((a) => a.selected).length;

          return (
            <div key={parcelle.id} className="mb-3 border-b pb-3">
              <div className="mb-1 flex items-center justify-between">
                <h4 className="text-xs font-medium">Parcelle: {parcelle.id}</h4>
                <small className="text-xs text-gray-500">
                  {selectedAddressCount}/{parcelleAddresses.length}
                </small>
              </div>
              <ParcelleSVG geometry={parcelle.geometry} maxHeight={120}/>
              {parcelleAddresses.map((address) => (
                <div key={address.id} className="mb-1 flex items-center">
                  <input
                    type="checkbox"
                    id={`addr-${address.id}`}
                    checked={address.selected}
                    onChange={() => onAddressCheck(address.id)}
                    className="mr-2 ml-1"
                  />
                  <label htmlFor={`addr-${address.id}`} className="flex items-center text-xs">
                    {address.geometry && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAddressClick(address.geometry!);
                        }}
                        className="group relative mr-1 inline-flex items-center justify-center rounded-md bg-rgpd-blue-light/10 p-1 transition-all duration-150 hover:scale-110 hover:bg-rgpd-blue-light/30 focus:ring-2 focus:ring-rgpd-blue-light focus:ring-offset-1 focus:outline-none"
                        title="Centrer la carte sur cette adresse"
                      >
                        <MapPinIcon className="h-3 w-3 text-rgpd-blue-dark group-hover:text-rgpd-blue-dark" />
                        <span className="pointer-events-none absolute top-1/2 left-full z-50 ml-2 -translate-y-1/2 rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          Voir sur la carte
                        </span>
                      </button>
                    )}
                    <span className="truncate">
                      {address.address}, {address.postalCode} {address.city}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          );
        })}

        {parcelles.length === 0 && (
          <p className="text-center text-xs text-gray-500">
            Cliquez sur la carte pour sélectionner des parcelles
          </p>
        )}
      </div>

      {/* Save button */}
      <div className="flex-shrink-0 border-t bg-gray-50 p-2">
        <button
          onClick={onSave}
          className="w-full rounded bg-rgpd-green px-4 py-2 text-sm font-medium text-white"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <ArrowPathIcon className="mr-2 inline h-4 w-4 animate-spin" />
              Enregistrement...
            </>
          ) : (
            'Enregistrer la localisation de la copropriété'
          )}
        </button>
      </div>
    </div>
  );
};

export default AddressPanel;
